// Sim send history page. Chronological log of SEND_LOG events with
// status / sim / user / device filters and CSV export. Scope (user
// metadata filters from the shell) applies before any local filters.
//
// Calculations: filter SEND_LOG by scope + filters, sort by sent_at DESC.
//
// Postgres mapping:
//   `simulations_sent` joined to `users`. Pagination via (sortBy,
//   direction, limit, offset). Status enum must include 'attachment'
//   (referenced by stage-depth scoring).
//
// Integration hook: `useSendHistoryQuery({ startDate, endDate, userEmail,
// status, simName, device, source, limit, offset })` cache 10 min.
import React, { useState, useMemo } from 'react';
import { Box, Button } from '@mui/material';
import { motion } from 'framer-motion';
import { SEND_LOG } from '../data';
import useDashboardData from '../hooks/useDashboardData';
import ActiveFilters from './ActiveFilters';
import DataTable from './DataTable';

const isIn = (val, vs) => !vs || (Array.isArray(vs) ? vs.includes(val) : val === vs);

function applyScopeToSendLog(log, scope) {
    const s = scope || {};
    return log.filter((e) => {
        if (!isIn(e.dept,     s.department)) return false;
        if (!isIn(e.location, s.location))   return false;
        if (!isIn(e.city,     s.city))       return false;
        if (!isIn(e.country,  s.country))    return false;
        if (!isIn(e.company,  s.company))    return false;
        if (!isIn(e.state,    s.state))      return false;
        if (!isIn(e.adGroup,  s.adGroup))    return false;
        if (s.user) {
            const userVals = Array.isArray(s.user) ? s.user : [s.user];
            const full = `${e.firstName} ${e.lastName}`;
            if (!userVals.some((v) => e.email === v || full === v)) return false;
        }
        return true;
    });
}

const fadeUp = { hidden: { opacity: 0, y: 12 }, visible: { opacity: 1, y: 0, transition: { duration: 0.3, ease: 'easeOut' } } };
const stagger = { hidden: {}, visible: { transition: { staggerChildren: 0.07 } } };

const STATUS_META = {
    clicked_2nd: { label: '2nd Stage Clicked', bg: 'var(--red-bg)',   text: 'var(--red-text)',   border: 'rgba(255,82,82,.25)',   dot: '#FF5252' },
    clicked:     { label: 'Clicked',           bg: 'var(--amber-bg)', text: 'var(--amber-text)', border: 'rgba(255,202,40,.3)',   dot: '#FFCA28' },
    no_click:    { label: 'No click',          bg: 'action.hover',    text: 'text.secondary',    border: 'divider',               dot: 'text.disabled' },
    reported:    { label: 'Reported',          bg: 'var(--green-bg)', text: 'var(--green-text)', border: 'rgba(102,187,106,.25)', dot: '#66BB6A' },
};

// Matches the active-users status chip in UsersPage: 4px-radius chip with a
// leading colored dot rather than the old pill, so status reads consistently
// across the roster and the send log.
function StatusBadge({ status }) {
    const m = STATUS_META[status] || STATUS_META.no_click;
    return (
        <Box sx={{
            display: 'inline-flex', alignItems: 'center', gap: 0.5,
            px: '6px', py: '1px', borderRadius: '4px',
            fontSize: 10, fontWeight: 600,
            bgcolor: m.bg, color: m.text,
            border: '1px solid', borderColor: m.border,
            whiteSpace: 'nowrap',
        }}>
            <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: m.dot, flexShrink: 0 }} />
            {m.label}
        </Box>
    );
}

function FilterSelect({ value, onChange, options, placeholder }) {
    return (
        <Box
            component="select"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            sx={{
                border: '1px solid', borderColor: 'divider', borderRadius: '4px',
                px: 1.25, py: '6px', fontSize: 11, color: value ? 'text.primary' : 'text.disabled',
                bgcolor: 'background.paper', outline: 'none', cursor: 'pointer', fontFamily: 'inherit',
                '&:focus': { borderColor: 'secondary.main' },
                appearance: 'none',
                backgroundImage: `url("data:image/svg+xml,%3Csvg width='8' height='5' viewBox='0 0 8 5' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M1 1l3 3 3-3' stroke='%236B7591' stroke-width='1.2' stroke-linecap='round'/%3E%3C/svg%3E")`,
                backgroundRepeat: 'no-repeat',
                backgroundPosition: 'right 8px center',
                pr: 3,
            }}
        >
            <option value="">{placeholder}</option>
            {options.map((o) => <option key={o} value={o}>{o}</option>)}
        </Box>
    );
}

function exportCSV(rows) {
    const headers = ['Name', 'Email', 'Simulation', 'Department', 'Location', 'Country', 'Date Sent', 'Device', 'Last Active', 'Status'];
    const lines = [headers.join(','), ...rows.map((e) => [
        `"${e.firstName} ${e.lastName}"`, `"${e.email}"`, `"${e.simName}"`,
        `"${e.dept}"`, `"${e.location}"`, `"${e.country}"`,
        `"${e.dateSent || ''}"`, `"${e.device || ''}"`, `"${e.lastActive || ''}"`,
        `"${STATUS_META[e.status]?.label || e.status}"`,
    ].join(','))];
    const blob = new Blob([lines.join('\n')], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = 'send-history.csv'; a.click();
    URL.revokeObjectURL(url);
}

export default function SendHistoryPage({ drill, scope, removeScope }) {
    const [filterSim, setFilterSim] = useState('');
    const [filterDept, setFilterDept] = useState('');
    const [filterStatus, setFilterStatus] = useState('');
    const [filterLocation, setFilterLocation] = useState('');
    const [filterDevice, setFilterDevice] = useState('');

    const scopedLog = useMemo(() => applyScopeToSendLog(SEND_LOG, scope), [scope]);
    // True total of delivery events (uncapped) from the sim rollup; the raw
    // event pull above is capped at the most-recent 20k, so show the real total.
    const totalQ = useDashboardData({ metric: 'simulations', aggregation: 'sims', startDate: '2015-01-01', endDate: '2099-12-31' });
    const trueTotal = Array.isArray(totalQ.data?.sims) ? totalQ.data.sims.reduce((a, r) => a + (r.sent || 0), 0) : null;

    const sims      = useMemo(() => [...new Set(scopedLog.map((e) => e.simName))].sort(), [scopedLog]);
    const depts     = useMemo(() => [...new Set(scopedLog.map((e) => e.dept))].sort(), [scopedLog]);
    const locations = useMemo(() => [...new Set(scopedLog.map((e) => e.location))].sort(), [scopedLog]);
    const devices   = useMemo(() => [...new Set(scopedLog.map((e) => e.device).filter(Boolean))].sort(), [scopedLog]);

    const rows = useMemo(() => scopedLog.filter((e) => {
        if (filterSim      && e.simName  !== filterSim)      return false;
        if (filterDept     && e.dept     !== filterDept)     return false;
        if (filterStatus   && e.status   !== filterStatus)   return false;
        if (filterLocation && e.location !== filterLocation) return false;
        if (filterDevice   && e.device   !== filterDevice)   return false;
        return true;
    }), [scopedLog, filterSim, filterDept, filterStatus, filterLocation, filterDevice]);

    const hasFilters = filterSim || filterDept || filterStatus || filterLocation || filterDevice;

    const columns = [
        { id: 'name',       label: 'Name',       always: true,    sortable: true, type: 'string', accessor: (e) => `${e.firstName} ${e.lastName}` },
        { id: 'email',      label: 'Email',      defaultOn: true, sortable: true, type: 'string', accessor: (e) => e.email },
        { id: 'simName',    label: 'Simulation', defaultOn: true, sortable: true, type: 'string', accessor: (e) => e.simName },
        { id: 'dept',       label: 'Department', defaultOn: true, sortable: true, type: 'string', accessor: (e) => e.dept },
        { id: 'location',   label: 'Location',   defaultOn: true, sortable: true, type: 'string', accessor: (e) => e.location },
        { id: 'country',    label: 'Country',    sortable: true,  type: 'string', accessor: (e) => e.country },
        { id: 'dateSent',   label: 'Date Sent',  defaultOn: true, sortable: true, type: 'string', accessor: (e) => e.dateSent },
        { id: 'device',     label: 'Device',     defaultOn: true, sortable: true, type: 'string', accessor: (e) => e.device || ', ' },
        { id: 'lastActive', label: 'Last Active',sortable: true,  type: 'string', accessor: (e) => e.lastActive || '' },
        { id: 'status',     label: 'Status',     defaultOn: true, sortable: true, type: 'string', accessor: (e) => e.status,
          render: (e) => <StatusBadge status={e.status} /> },
    ];

    return (
        <motion.div variants={stagger} initial="hidden" animate="visible" style={{ padding: '0 24px 48px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <ActiveFilters scope={scope} removeScope={removeScope} noPadX />
            <motion.div variants={fadeUp}>
                <DataTable
                    title="Send History"
                    subtitle={(() => {
                        const cap = SEND_LOG.length;
                        if (trueTotal != null && trueTotal > cap) return `${rows.length.toLocaleString()} shown \u2014 most recent ${cap.toLocaleString()} of ${trueTotal.toLocaleString()} events (newest first)`;
                        return `${rows.length.toLocaleString()} of ${scopedLog.length.toLocaleString()} simulation delivery events${scopedLog.length !== SEND_LOG.length ? ` (filtered from ${SEND_LOG.length.toLocaleString()})` : ''}`;
                    })()}
                    columns={columns}
                    rows={rows}
                    rowKey={(e) => `${e.email}-${e.simName}-${e.dateSent}`}
                    onRowClick={(e) => drill('user-detail', `${e.firstName} ${e.lastName}`, { type: 'user', value: e.email })}
                    initialSort={{ by: 'dateSent', dir: 'desc' }}
                    storageKey="rbx-send-history-cols"
                    emptyMessage="No matching delivery events"
                    headerActions={
                        <Button
                            onClick={() => exportCSV(rows)}
                            variant="outlined" size="small"
                            sx={{ borderRadius: '4px', borderColor: 'secondary.main', color: 'secondary.main', fontSize: 11, textTransform: 'none', whiteSpace: 'nowrap', '&:hover': { bgcolor: 'secondary.main', color: 'common.white' } }}
                        >
                            Export CSV ({rows.length})
                        </Button>
                    }
                    toolbar={
                        <>
                            <FilterSelect value={filterSim}      onChange={setFilterSim}      options={sims}      placeholder="All simulations" />
                            <FilterSelect value={filterDept}     onChange={setFilterDept}     options={depts}     placeholder="All departments" />
                            <FilterSelect value={filterLocation} onChange={setFilterLocation} options={locations} placeholder="All locations" />
                            <FilterSelect value={filterDevice}   onChange={setFilterDevice}   options={devices}   placeholder="All devices" />
                            <FilterSelect
                                value={filterStatus} onChange={setFilterStatus}
                                options={['clicked_2nd', 'clicked', 'no_click', 'reported']}
                                placeholder="All statuses"
                            />
                            {hasFilters && (
                                <Button size="small" onClick={() => { setFilterSim(''); setFilterDept(''); setFilterStatus(''); setFilterLocation(''); setFilterDevice(''); }}
                                    sx={{ fontSize: 10, color: 'text.disabled', textTransform: 'none', minWidth: 0, px: 1, '&:hover': { color: 'text.secondary' } }}>
                                    Clear
                                </Button>
                            )}
                        </>
                    }
                />
            </motion.div>
        </motion.div>
    );
}
